<script setup lang="ts">
    import { getLibraryStats, LibraryStatsResponse } from '@/shared/helpers/api';
    import { CategoryScale, Chart, Legend, LinearScale, PointElement, Title, Tooltip, BarElement, ArcElement, Colors, ChartOptions } from 'chart.js';
    import { Bar as BarChart, Doughnut as DoughnutChart } from 'vue-chartjs';
    import { http } from '@/shared/httpWrapper';
    import { computed, onMounted, ref } from 'vue';
    import { formatNumber } from '@/formatters';

    Chart.register(
        CategoryScale,
        LinearScale,
        PointElement,
        Title,
        Tooltip,
        Legend,
        BarElement,
        ArcElement,
        Colors
    );

    const props = defineProps({
        libraryName: {
            type: String,
            required: false,
            default: ''
        }
    });

    const loading = ref<boolean>(true);
    const data = ref<LibraryStatsResponse | null>(null);
    const selectedStats = ref<'Clients' | 'RequestsByDay'>('Clients');

    const requestsByClientOptions = {
        plugins: {
            colors: {
                enabled: true
            },
            legend: {
                position: 'right',
                title: {
                    text: 'Client applications',
                    display: true,
                    padding: 10,
                    font: {
                        weight: 'bold'
                    }
                }
            }
        }
    } as ChartOptions<'doughnut'>;

    const requestsByDayOptions = {
        plugins: {
            colors: {
                enabled: true
            },
            scales: {
                x: {
                    stacked: true
                },
                y: {
                    stacked: true
                }
            }
        }
    } as ChartOptions<'bar'>;

    const monthlyRequestCountByApps = computed(() => data.value ? data.value.requestCountApplications : '..');
    const monthlyRequestCountByOthers = computed(() => data.value ? data.value.requestCountTotal - data.value.requestCountApplications : '..');
    const averageDailyRequests = computed(() => data.value ? Math.round(data.value.requestCountTotal / data.value.requestsByDay.labels.length) : '..');

    onMounted(async () => {
        data.value = await getLibraryStats(http, props.libraryName);

        data.value.requestsByDay.datasets.forEach(x => x.stack = 'main');
        data.value.requestsByDay.datasets[0].backgroundColor = '#36A2EB';
        data.value.requestsByDay.datasets[1].backgroundColor = '#FF9F40';
        loading.value = false;
    });

</script>

<template>
    <nav class="level">
        <div class="level-item has-text-centered">
            <div>
                <p class="heading">
                    Client applications
                </p>
                <p class="title">
                    {{ data?.applicationsCount ?? '..' }}
                </p>
            </div>
        </div>
        <div class="level-item has-text-centered">
            <div>
                <p class="heading">
                    Monthly requests by apps
                </p>
                <p class="title">
                    {{ formatNumber(monthlyRequestCountByApps) }}
                </p>
            </div>
        </div>
        <div class="level-item has-text-centered">
            <div>
                <p class="heading">
                    Monthly requests by other
                </p>
                <p class="title">
                    {{ formatNumber(monthlyRequestCountByOthers) }}
                </p>
            </div>
        </div>
        <div class="level-item has-text-centered">
            <div>
                <p class="heading">
                    Average daily requests
                </p>
                <p class="title">
                    {{ formatNumber(averageDailyRequests) }}
                </p>
            </div>
        </div>
    </nav>

    <div>
        <b-field label="Select report:">
            <b-radio-button
                v-model="selectedStats"
                native-value="Clients">
                <b-icon
                    icon="chart-arc"
                    size="is-small" />
                <span>Client applications</span>
            </b-radio-button>
            <b-radio-button
                v-model="selectedStats"
                native-value="RequestsByDay">
                <b-icon
                    icon="chart-bar"
                    size="is-small" />
                <span>Requests by day</span>
            </b-radio-button>
        </b-field>
    </div>
    <hr>
    <section v-if="selectedStats === 'Clients'">
        <doughnut-chart
            v-if="!loading"
            :data="data!.requestsByClient"
            :options="requestsByClientOptions"
            style="max-width: 1000px; max-height: 500px;" />
    </section>
    <section v-if="selectedStats === 'RequestsByDay'">
        <bar-chart
            v-if="!loading"
            :data="data!.requestsByDay"
            :options="requestsByDayOptions"
            style="max-width: 1000px; max-height: 500px;" />
    </section>
</template>
